import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

// Actions
import { getMapViewExploreSites } from "../../../actions/map-view/media-sites/ExploreSitesActions";
import { getCityOverview } from "../../../actions/campaign-report/CampaignCityReportActions";

// Constants & Utils
import {
  DefaultPagination,
  LocalStorageKeys,
  OrganizationIdsMapping,
  PageSize,
  PatrikaSellerId,
} from "../../../constants/GeneralConstants";
import { SearchFilterAndSortMediaSection } from "./MediaSitesUtils";
import { checkIsOrgSpecific } from "../../../utils/OrgUtils";
import { getItem } from "../../../utils/localstorage";

// Components
import Spinner from "../../../components/spinner/Spinner";

// Page sections
import ExploreSitesTable from "./ExploreSitesTable";

// only for Mantaray & Patrika
function useGetCityOverview(cityId, isOrgMedia, isPatrikaSpecific) {
  const dispatch = useDispatch();
  const { id: campaignId } = useParams();

  // for adding org inventory media to campaign, we need campaign city duration
  // calling api only when "isOrgMedia = true" :: inventory medias
  // and when campaignId present :: "MapView"
  const isCallCitySummary = Boolean(
    (isOrgMedia && campaignId) || (isPatrikaSpecific && campaignId)
  );

  useEffect(() => {
    if (isCallCitySummary) {
      dispatch(getCityOverview(campaignId, cityId));
    }
  }, [dispatch, isCallCitySummary]);

  return;
}

/**
 * Explore SItes Section
 */
function ExploreSitesSection({ listingViewLabels, cityId, isOrgMedia }) {
  const dispatch = useDispatch();

  // State
  const [exploreSitesList, setExploreSitesList] = useState([]);

  // Selector
  const mediaList = useSelector(
    (state) => state.mapViewExploreSites.exploreSites
  );
  const loading = useSelector((state) => state.mapViewExploreSites.loading);

  const pagination = useSelector(
    (state) => state.mapViewExploreSites.pagination
  );

  // user info
  const user = JSON.parse(getItem(LocalStorageKeys.USER));
  const { orgId } = user || {};

  // check patrika specific
  const { patrika } = OrganizationIdsMapping;
  const isPatrikaSpecific = checkIsOrgSpecific(orgId, patrika);

  // Pagination
  const { pageNumber } = DefaultPagination;
  const { inventoryMediaList: pageSize } = PageSize;

  // inventory media sites
  useEffect(() => {
    dispatch(
      getMapViewExploreSites(cityId, isOrgMedia, orgId, pageNumber, pageSize)
    );
  }, [dispatch, isOrgMedia, cityId]);

  // getting city overview (only for mantaray & patrika)
  useGetCityOverview(cityId, isOrgMedia, isPatrikaSpecific);

  // patrika inventory
  const getPatrikaInventory = useCallback(
    (mediaList) => {
      return mediaList.filter((media) => media.sellerId === PatrikaSellerId);
    },
    [isPatrikaSpecific]
  );

  const filteredMediaList = isPatrikaSpecific
    ? getPatrikaInventory(mediaList)
    : mediaList;

  // function
  function loadCityMedia(pageNumber, pageSize) {
    dispatch(
      getMapViewExploreSites(cityId, isOrgMedia, orgId, pageNumber, pageSize)
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner />
      </div>
    );
  }

  const isMediaSitesAvailable =
    filteredMediaList.length < 1 || exploreSitesList.length < 1;

  return (
    <>
      {/* Search , Sort and filter */}
      <SearchFilterAndSortMediaSection
        mediaList={filteredMediaList}
        setExploreSitesList={setExploreSitesList}
        isOrgMedia={isOrgMedia}
        listingViewLabels={listingViewLabels}
      />
      <hr className="divider my-2" />

      <ExploreSitesTable
        mediaList={exploreSitesList}
        pagination={pagination}
        loadCityMedia={loadCityMedia}
        isOrgMedia={isOrgMedia}
        isPatrikaSpecific={isPatrikaSpecific}
      />

      {/* If there is no sites found */}
      {isMediaSitesAvailable && (
        <p className="text-center mt-3 font-italic">No sites are available</p>
      )}
    </>
  );
}

export default ExploreSitesSection;
